import React, {useEffect, useMemo, useState} from 'react';
import SlideAnimation from "./SlideAnimation";
import { DayPickerRangeController } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import moment from "moment";
import {START_DATE, END_DATE, VERTICAL_ORIENTATION, HORIZONTAL_ORIENTATION} from "react-dates/constants"
import 'react-dates/initialize';

import "../../../sass/components/react-dates-overrides.scss"
import ResizeDetector from "react-resize-detector";
import { isDayFullyClosed } from "../../utils/closingPeriods";

moment.locale('fr')
const pickerMode = {
    start : "start",
    end : "end"
}

const HORIZONTAL_BREAKPOINT = 600
const DEFAULT_DAY_SIZE = 39
const DEFAULT_MONTH_PADDING = 13
const MOBILE_MONTH_PADDING = 6
const MOBILE_MIN_DAY_SIZE = 32
const MOBILE_MAX_DAY_SIZE = 52

// react-dates lays the calendar out at a fixed `7 * daySize + 2 * horizontalMonthPadding`
// width, so on a phone it leaves large empty gutters on both sides. Scale the cells to
// the measured container width instead, capped so the grid stays usable on tablets.
const getMobileDaySize = (width) => {
    if (!width) { return DEFAULT_DAY_SIZE }
    const available = width - (2 * MOBILE_MONTH_PADDING) - 1
    return Math.min(MOBILE_MAX_DAY_SIZE, Math.max(MOBILE_MIN_DAY_SIZE, Math.floor(available / 7)))
}

// In vertical mode react-dates derives the scroll window height from the month width
// (1.75 * width), so widening the cells would push the calendar past the viewport.
// Pin it to the space the fullscreen modal actually leaves (header + footer ≈ 220px).
const getVerticalHeight = () => {
    if (typeof window === 'undefined') { return null }
    return Math.max(320, window.innerHeight - 220)
}

const DatePicker = ({ mode, startDateStr, endDateStr, onSelect, name, minDate, maxDate, unavailableDays = [], closingPeriods = [] }) => {

    const [state, setState] = useState({
        start_date : moment(startDateStr, 'YYYY-MM-DD').isValid() ? moment(startDateStr, 'YYYY-MM-DD') : null,
        end_date: null,
        focused : START_DATE
    });

    const pickerMinDate = useMemo(() => {
        if(minDate) {
            return minDate
        } else {
            return (mode === pickerMode.end && state.start_date) ? state.start_date : moment()
        }
    }, [minDate]);

    const pickerMaxDate = useMemo(() => {
        if(maxDate) {
            return maxDate
        } else {
            return (mode === pickerMode.start && state.end_date) ? state.end_date : null
        }
    }, [maxDate]);

    useEffect(() => {
        setState(prevState => {
            const newState = {...prevState}
            if(startDateStr) {
                const momentStart = moment(startDateStr, 'YYYY-MM-DD')
                if(momentStart.isValid()) {
                    newState.start_date = momentStart
                }
            }
            if( endDateStr) {
                const momentEnd = moment(endDateStr,'YYYY-MM-DD')
                if(momentEnd.isValid()) {
                    newState.end_date = momentEnd
                }
            }
            return newState
        })
    }, [startDateStr, endDateStr]);

    useEffect(() => {
        if(mode === pickerMode.end) {
            setState(prevState =>({
                ...prevState, focused: END_DATE
            }))
        } else {
            setState(prevState =>({
                ...prevState, focused: START_DATE
            }))
        }
    }, [mode])

    const handleChange = ({startDate, endDate}) => {
        console.log(state.focused, startDate, endDate)
        setState(prevState => ({
            ...prevState,
            start_date: startDate,
            end_date: endDate
        }))
        if(mode === pickerMode.start) {
            onSelect && onSelect({name, value: startDate.format("YYYY-MM-DD") })
        } else {
            onSelect && onSelect({name, value: endDate.format("YYYY-MM-DD") })
        }
    }

    const handleInitialMonth = () => {
        if (state.start_date) {
           return state.start_date
        }
        return moment()
    }

    return (
        <ResizeDetector
            handleWidth
            render={({ width }) => {
                const isHorizontal = width > HORIZONTAL_BREAKPOINT
                return (
                    <SlideAnimation className={"d-flex justify-content-center align-items-center h-100 w-100"}>
                        <DayPickerRangeController
                            noBorder
                            hideKeyboardShortcutsPanel
                            orientation={isHorizontal ? HORIZONTAL_ORIENTATION : VERTICAL_ORIENTATION}
                            daySize={isHorizontal ? DEFAULT_DAY_SIZE : getMobileDaySize(width)}
                            horizontalMonthPadding={isHorizontal ? DEFAULT_MONTH_PADDING : MOBILE_MONTH_PADDING}
                            verticalHeight={isHorizontal ? null : getVerticalHeight()}
                            numberOfMonths={2}
                            minDate={pickerMinDate}
                            maxDate={pickerMaxDate}
                            startDate={state.start_date}
                            endDate={state.end_date}
                            onDatesChange={handleChange}
                            focusedInput={state.focused}
                            minimumNights={0}
                            initialVisibleMonth={handleInitialMonth}
                            isDayBlocked={(date) => {
                                let blocked = false
                                const dateStr = date.format('YYYY-MM-DD')
                                const nowStr = moment().startOf('day').format('YYYY-MM-DD')
                                const isToday = moment().format('YYYY-MM-DD') === dateStr
                                if (mode === pickerMode.end && state.start_date) {
                                    blocked = dateStr < moment(state.start_date).format('YYYY-MM-DD')
                                }
                                // `unavailableDays` = weekday indices 0–6 (Sun–Sat), same as date-fns getDay.
                                // Do NOT use `date.format('d')` — in Moment that is day-of-month (1–31), not weekday.
                                if (unavailableDays?.length > 0 && unavailableDays.includes(date.day())) {
                                    blocked = true
                                }
                                if (isDayFullyClosed(date.toDate(), closingPeriods)) {
                                    blocked = true
                                }
                                blocked = blocked || (dateStr < nowStr && !isToday)
                                return blocked
                            }}
                        />
                    </SlideAnimation>
                )
            }}
        />
    );
};

export default DatePicker;
